import { createFileRoute, Link } from "@tanstack/react-router";
import { makeSeoDescription } from "@/lib/seo-description";
import { Breadcrumbs, buildBreadcrumbLd } from "@/components/site/Breadcrumbs";

const SITE = "https://lifewestway.com";
const TITLE = "Privacy Policy | LifeWestWay";
const DESC =
  "How LifeWestWay collects, uses, and protects information on lifewestway.com — including cookies, Google AdSense advertising, analytics, and your rights under GDPR and CCPA.";
const URL_ = `${SITE}/privacy`;
const BREADCRUMBS = [{ label: "Home", href: "/" }, { label: "Privacy Policy" }];

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/privacy" }],
    scripts: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify(buildBreadcrumbLd(BREADCRUMBS)),
      },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <header className="mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Legal</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground sm:text-5xl">
          Privacy Policy
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">Last Updated: July 27, 2026</p>
      </header>

      <div className="space-y-6 text-[15px] leading-relaxed text-foreground/80">
        <p>
          This Privacy Policy explains how{" "}
          <strong className="text-foreground">LifeWestWay</strong> (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;) collects, uses, and protects information when you visit{" "}
          <Link title="Return to the LifeWestWay homepage" to="/" className="text-accent hover:underline">
            lifewestway.com
          </Link>{" "}
          (the &ldquo;Site&rdquo;).
        </p>

        {/* 1. Information We Collect */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          1. Information We Collect
        </h2>
        <h3 className="mt-6 font-display text-xl font-semibold text-foreground">
          a) Information You Provide Directly
        </h3>
        <p>
          If you contact us via email or any contact form, we may collect your name, email address, and
          any information you choose to include in your message.
        </p>
        <h3 className="mt-6 font-display text-xl font-semibold text-foreground">
          b) Information Collected Automatically
        </h3>
        <p>Like most websites, we automatically collect certain information when you visit, including:</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>IP address (typically anonymized or truncated)</li>
          <li>Browser type and device information</li>
          <li>Pages visited, time spent on pages, and referring URLs</li>
          <li>General geographic location (country/city level)</li>
        </ul>
        <h3 className="mt-6 font-display text-xl font-semibold text-foreground">
          c) Cookies and Similar Technologies
        </h3>
        <p>
          We use cookies and similar tracking technologies to operate the Site, remember preferences,
          and analyze traffic. You can control or disable cookies through your browser settings,
          though this may affect some Site functionality.
        </p>

        {/* 2. Google AdSense & Advertising */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          2. Google AdSense & Advertising
        </h2>
        <p>
          This Site displays advertisements served by{" "}
          <strong className="text-foreground">Google AdSense</strong> and may work with other
          third-party advertising partners.
        </p>
        <p>
          Google, as a third-party vendor, uses cookies (including the DoubleClick cookie) to serve ads
          based on your prior visits to this Site or other websites.
        </p>
        <p>
          Google's use of advertising cookies enables it and its partners to serve ads based on
          your visit to this Site and/or other sites on the Internet.
        </p>
        <p>
          You may opt out of personalized advertising by visiting{" "}
          <a title="Open Google Ads Settings in a new tab"
            href="https://www.google.com/settings/ads"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            Google's Ads Settings
          </a>{" "}
          or the{" "}
          <a title="Open the NAI advertising opt-out page in a new tab"
            href="https://optout.networkadvertising.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            Network Advertising Initiative opt-out page
          </a>
          .
        </p>
        <p>
          Third-party vendors, including Google, may also use cookies to serve ads based on someone's
          past visits to this website.
        </p>
        <p>
          For more information on how Google uses data when you use our partners' sites or apps,
          visit{" "}
          <a title="Read how Google uses data from partner sites"
            href="https://policies.google.com/technologies/partner-sites"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            Google's Privacy & Terms site
          </a>
          .
        </p>

        {/* 3. Analytics */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">3. Analytics</h2>
        <p>
          We may use third-party analytics services (such as Google Analytics) to help us understand
          how visitors use the Site. These services may use cookies and similar technologies to
          collect and analyze information about Site usage. This data is generally aggregated and does
          not personally identify you.
        </p>

        {/* 4. How We Use Information */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          4. How We Use Information
        </h2>
        <p>We use collected information to:</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>Operate, maintain, and improve the Site</li>
          <li>Understand which content is most useful to visitors</li>
          <li>Respond to inquiries sent through our Contact Us page</li>
          <li>Display relevant advertising through Google AdSense and other ad partners</li>
          <li>Detect and prevent fraud, abuse, or security issues</li>
        </ul>

        {/* 5. Data Sharing */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">5. Data Sharing</h2>
        <p>
          We do not sell your personal information. We may share information with:
        </p>
        <ul className="ml-5 list-disc space-y-2">
          <li>Service providers who help operate the Site (hosting, analytics, email)</li>
          <li>Advertising partners (such as Google) as described above</li>
          <li>Legal authorities, if required by law or to protect our rights</li>
        </ul>

        {/* 6. Your Rights */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">6. Your Rights</h2>
        <p>
          Depending on your location, you may have rights under applicable law (such as the GDPR for
          EU/UK/EEA residents, or the CCPA for California residents), including the right to:
        </p>
        <ul className="ml-5 list-disc space-y-2">
          <li>Access the personal information we hold about you</li>
          <li>Request correction or deletion of your information</li>
          <li>Object to or restrict certain processing</li>
          <li>Opt out of the sale or sharing of personal information (where applicable)</li>
          <li>Opt out of personalized advertising</li>
        </ul>
        <p>
          To exercise these rights, contact us at the email listed in our{" "}
          <Link title="Contact LifeWestWay about privacy" to="/contact" className="text-accent hover:underline">
            Contact Us
          </Link>{" "}
          page.
        </p>

        {/* 7. Children's Privacy */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          7. Children's Privacy
        </h2>
        <p>
          This Site is not directed at children under 13 (or the relevant minimum age in your
          jurisdiction), and we do not knowingly collect personal information from children.
        </p>

        {/* 8. International Visitors */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          8. International Visitors
        </h2>
        <p>
          If you are visiting from outside our hosting jurisdiction, please be aware that your
          information may be transferred to, stored, and processed in a different country, which may
          have different data protection laws than your own.
        </p>

        {/* 9. Data Retention */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">9. Data Retention</h2>
        <p>
          We retain information only as long as necessary to fulfill the purposes described in this
          policy, comply with legal obligations, resolve disputes, and enforce our agreements.
        </p>

        {/* 10. Changes to This Policy */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          10. Changes to This Policy
        </h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices or
          for legal, operational, or regulatory reasons. The &ldquo;Last Updated&rdquo; date at the top
          of this page reflects the most recent revision.
        </p>

        {/* 11. Contact Us */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">11. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or how we handle your information, please
          visit our{" "}
          <Link title="Contact LifeWestWay about privacy" to="/contact" className="text-accent hover:underline">
            Contact Us
          </Link>{" "}
          page.
        </p>

        <p className="mt-8 text-sm leading-6 text-muted-foreground">
          This Privacy Policy is provided for general informational purposes and is intended to comply
          with common requirements for websites using Google AdSense and third-party advertising,
          including disclosure of cookie use and opt-out options. It is not a substitute for legal
          advice — we recommend having this policy reviewed by a qualified attorney familiar with the
          specific privacy laws applicable to your website's audience (including GDPR, UK GDPR,
          and CCPA/CPRA as relevant).
        </p>
      </div>

    </div>
  );
}
